// Multi-tenant host helpers (Omni Workspace). The hostname only sets the UI
// context — the SERVER decides a client's tenant from its trusted records.
import { axiosInstance } from "./axios";

export const OMNI_ROOT = "omniworkspace.app";

export const currentHost = () => window.location.hostname.toLowerCase();
export const isOmniHost = (h = currentHost()) => h === OMNI_ROOT || h.endsWith(`.${OMNI_ROOT}`);
export const isOmniCentral = (h = currentHost()) => h === OMNI_ROOT || h === `www.${OMNI_ROOT}`;
export const isOmniTenantHost = (h = currentHost()) =>
  isOmniHost(h) && !isOmniCentral(h) && h !== `api.${OMNI_ROOT}`;

export interface TenantInfo {
  name: string;
  slug: string;
  subdomain: string;
  portalUrl?: string;
  companyName?: string;
  legalName?: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  supportEmail?: string;
  supportPhone?: string;
  footerText?: string;
  // Resolved Environment Configuration (design tokens + portal + features).
  portalName?: string;
  secondaryLogoUrl?: string;
  loginLogoUrl?: string;
  backgroundColor?: string;
  textColor?: string;
  timezone?: string;
  currency?: string;
  language?: string;
  features?: Record<string, boolean>;
}

const hexToRgbTriple = (hex: string): string | null => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

const darken = (hex: string, f = 0.72): string => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const d = (v: number) => Math.round(v * f);
  return `#${((d((n >> 16) & 255) << 16) | (d((n >> 8) & 255) << 8) | d(n & 255)).toString(16).padStart(6, "0")}`;
};

// Apply a tenant's visual identity at runtime: theme colors (CSS variables the
// Tailwind tokens read), page title, favicon and optional font. Configuration-
// driven — the SAME code renders every brand.
export const applyBranding = (t?: TenantInfo | null) => {
  if (!t) return;
  const root = document.documentElement;
  const primary = t.primaryColor || "#0058c3";
  const rgb = hexToRgbTriple(primary);
  if (rgb) {
    root.style.setProperty("--brand-primary", rgb);
    root.style.setProperty("--brand-surface-tint", rgb);
  }
  const accentRgb = hexToRgbTriple(t.accentColor || primary);
  if (accentRgb) root.style.setProperty("--brand-primary-container", accentRgb);
  root.style.setProperty("--brand-hex", primary);
  root.style.setProperty("--brand-hex-dark", darken(primary));
  // Deep shades of the primary for the login showcase panel.
  root.style.setProperty("--brand-panel-light", darken(primary, 0.55));
  root.style.setProperty("--brand-panel-mid", darken(primary, 0.38));
  root.style.setProperty("--brand-panel-dark", darken(primary, 0.22));
  if (t.secondaryColor) root.style.setProperty("--brand-secondary-hex", t.secondaryColor);
  // Surface + text tokens straight from the environment configuration.
  const bgRgb = t.backgroundColor ? hexToRgbTriple(t.backgroundColor) : null;
  if (bgRgb) root.style.setProperty("--brand-background", bgRgb);
  if (t.backgroundColor) root.style.setProperty("--brand-background-hex", t.backgroundColor);
  const textRgb = t.textColor ? hexToRgbTriple(t.textColor) : null;
  if (textRgb) root.style.setProperty("--brand-on-surface", textRgb);
  if (t.textColor) root.style.setProperty("--brand-text-hex", t.textColor);
  if (t.fontFamily) document.body.style.fontFamily = `'${t.fontFamily}', sans-serif`;
  if (t.companyName) document.title = t.companyName;
  const fav = t.faviconUrl || "";
  if (fav) {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = fav;
  }
};

// Ask the backend which branded workspace this hostname is. Unknown/inactive
// subdomains resolve to null (they must never fall back to a tenant).
export const resolveTenantForHost = async (): Promise<{ central: boolean; tenant: TenantInfo | null }> => {
  try {
    const { data } = await axiosInstance.get(`/tenants/resolve?host=${encodeURIComponent(currentHost())}`);
    return { central: !!data.central, tenant: data.tenant || null };
  } catch {
    return { central: isOmniCentral(), tenant: null };
  }
};
